import { Camera, Copy, ImagePlus, Link } from "lucide-react";
import { CommunityLogo } from "@/src/common/components/app-mock/community-logo";
import { MOCK_COMMUNITY } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockBreadcrumbs, MockButton, MockCard, MockField } from "@/src/common/components/app-mock/primitives";
import {
	COMMUNITY_VARIANTS,
	type CommunityVariant,
	getVariantPalette,
	useCommunityVariant,
} from "@/src/common/components/product-demo/community-variant-context";

const FEATURE_TOGGLES: ReadonlyArray<{ label: string; on: boolean }> = [
	{ label: "Feed", on: true },
	{ label: "Messages", on: true },
	{ label: "Meetings", on: true },
	{ label: "Events", on: true },
	{ label: "Videos", on: true },
	{ label: "Paid events", on: false },
];

function ThemeDot({ color }: { color: string }) {
	return <span className="size-2.5 rounded-full" style={{ background: color }} />;
}

interface ThemeSelectorProps {
	interactive?: boolean;
}

function ThemeSelector({ interactive }: ThemeSelectorProps) {
	const { variant, setVariant } = useCommunityVariant();
	const renderOption = (option: CommunityVariant) => {
		const palette = getVariantPalette(option);
		const isSelected = option === variant;
		const classes = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition";
		const style = isSelected
			? { background: "#171717", borderColor: "#171717", color: "#fff" }
			: { borderColor: MOCK_TOKENS.default300, color: "#171717" };
		return interactive ? (
			<button key={option} type="button" aria-pressed={isSelected} onClick={() => setVariant(option)} className={classes} style={style}>
				<ThemeDot color={palette.accent} />
				{palette.label}
			</button>
		) : (
			<span key={option} className={classes} style={style}>
				<ThemeDot color={palette.accent} />
				{palette.label}
			</span>
		);
	};
	return (
		<div data-mock="theme-selector">
			<p className="mb-2 text-[11px] font-medium text-neutral-900">Theme</p>
			{interactive ? (
				<fieldset className="flex flex-wrap gap-1.5" aria-label="Community theme">
					{COMMUNITY_VARIANTS.map(renderOption)}
				</fieldset>
			) : (
				<div className="flex flex-wrap gap-1.5" aria-hidden="true">
					{COMMUNITY_VARIANTS.map(renderOption)}
				</div>
			)}
		</div>
	);
}

interface WebCommunitySettingsScreenProps {
	interactive?: boolean;
	sections?: ReadonlyArray<"identity" | "appearance" | "features">;
}

export function WebCommunitySettingsScreen({
	interactive,
	sections = ["identity", "appearance", "features"],
}: WebCommunitySettingsScreenProps) {
	const { palette } = useCommunityVariant();
	return (
		<div className="space-y-3" data-mock="community-settings">
			<MockBreadcrumbs items={["Admin", "Community", MOCK_COMMUNITY.name]} />
			<div
				className="relative h-20 overflow-hidden rounded-xl"
				style={{ background: `linear-gradient(to right, color-mix(in oklab, ${palette.accent} 30%, transparent), ${palette.accentSoft})` }}
				aria-hidden="true"
			>
				<span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-lg bg-black/50 px-2 py-1 text-[10px] font-medium text-white">
					<ImagePlus className="size-3" />
					Edit
				</span>
			</div>
			<div className="flex items-center justify-between gap-2">
				<p className="text-[10px] text-neutral-500">
					{MOCK_COMMUNITY.memberCount.toLocaleString("en-US")} Members · {MOCK_COMMUNITY.createdLabel}
				</p>
				<MockButton size="xs" variant="outline" icon={Link}>
					Invite Link
				</MockButton>
			</div>

			{sections.includes("identity") ? (
				<MockCard dataMock="settings-identity">
					<p className="text-xs font-semibold text-neutral-900">Identity</p>
					<div className="mt-3 space-y-3">
						<div className="flex items-center gap-3">
							<span className="relative inline-flex" aria-hidden="true">
								<CommunityLogo size={52} rounded="rounded-full" />
								<span
									className="absolute -bottom-0.5 -right-0.5 inline-flex size-5 items-center justify-center rounded-full bg-white text-neutral-700"
									style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
								>
									<Camera className="size-3" />
								</span>
							</span>
							<div>
								<p className="text-[11px] font-medium text-neutral-900">Community Logo</p>
								<p className="text-[10px] text-neutral-500">PNG or JPG, at least 256 × 256</p>
							</div>
						</div>
						<MockField label="Community name" placeholder="Enter community name" value={MOCK_COMMUNITY.name} />
						<div className="flex items-end gap-2">
							<MockField className="flex-1" label="Invite Link" placeholder="slug" value={MOCK_COMMUNITY.slug} suffix=".upsquad.us" />
							<MockButton size="sm" variant="outline" icon={Copy}>
								Copy Link
							</MockButton>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-[11px] font-medium text-neutral-800">Community description</span>
							<p
								className="rounded-xl px-3 py-2 text-[11px] leading-relaxed text-neutral-800"
								style={{ background: MOCK_TOKENS.surfaceSecondary, boxShadow: MOCK_TOKENS.surfaceShadow }}
							>
								{MOCK_COMMUNITY.description}
							</p>
						</div>
					</div>
				</MockCard>
			) : null}

			{sections.includes("appearance") ? (
				<MockCard dataMock="settings-appearance">
					<p className="text-xs font-semibold text-neutral-900">Appearance</p>
					<div className="mt-3">
						<ThemeSelector interactive={interactive} />
					</div>
				</MockCard>
			) : null}

			{sections.includes("features") ? (
				<MockCard dataMock="settings-features">
					<p className="text-xs font-semibold text-neutral-900">Features</p>
					<ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3" aria-hidden="true">
						{FEATURE_TOGGLES.map((toggle) => (
							<li key={toggle.label} className="flex items-center justify-between gap-2 text-[11px] text-neutral-800">
								{toggle.label}
								<span
									className="relative inline-flex h-4 w-7 items-center rounded-full p-0.5"
									style={{ background: toggle.on ? palette.accent : MOCK_TOKENS.default300 }}
								>
									<span className={`size-3 rounded-full bg-white transition ${toggle.on ? "translate-x-3" : ""}`} />
								</span>
							</li>
						))}
					</ul>
				</MockCard>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3" style={{ borderColor: MOCK_TOKENS.border }}>
				<MockButton size="sm" variant="ghost">
					Reset
				</MockButton>
				<MockButton size="sm">Save Changes</MockButton>
			</div>
		</div>
	);
}
